import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useSubscription } from "@/hooks/queries/use-billing";

/**
 * Routes a `billing-only` organization to /billing (ENG-200-4).
 *
 * An `incomplete` organization is refused on every non-allowlisted route, so
 * without this it would see an app full of error states and have to find the
 * billing screen on its own. The redirect turns that into a single coherent
 * destination.
 *
 * Three rules keep it from becoming a trap:
 *
 *  1. **The billing route is exempt**, or the redirect targets itself and loops
 *     forever. Pinned by its own test — this is the classic way a guard like
 *     this ships broken, and it is invisible until someone actually lapses.
 *  2. **Only `billing-only` redirects.** A `read-only` organization keeps
 *     browsing; it can still read everything it has made. Redirecting it would
 *     take away access that was deliberately preserved.
 *  3. **Never redirect while loading or on error.** `access` defaults to `full`
 *     until the server answers, so a slow or failed request cannot bounce a
 *     paying customer out of the page they asked for.
 */
export function BillingOnlyGuard({ children }: { children: ReactNode }) {
  const { data } = useSubscription();
  const location = useLocation();

  const isOnBillingRoute = location.pathname.startsWith("/billing");
  const shouldRedirect =
    data !== undefined &&
    !data.organizationless &&
    data.access === "billing-only" &&
    !isOnBillingRoute;

  if (shouldRedirect) {
    return <Navigate to="/billing" replace />;
  }

  return <>{children}</>;
}
